import type { VoiceCommandResult } from './voiceCommandClient';

export type VoiceDataResource =
  | 'contacts'
  | 'expenses'
  | 'invoices'
  | 'accounts'
  | 'bank_accounts'
  | 'journal_entries'
  | 'inventory'
  | 'quotes'
  | 'transfers'
  | 'tax_periods'
  | 'payments';

const ACTION_RESOURCE_MAP: Record<string, VoiceDataResource[]> = {
  create_expense: ['expenses', 'contacts', 'journal_entries'],
  record_expense: ['expenses', 'contacts', 'journal_entries'],
  record_travel_expense: ['expenses', 'contacts', 'journal_entries'],
  record_office_expense: ['expenses', 'contacts', 'journal_entries'],
  update_expense: ['expenses', 'journal_entries'],
  delete_expense: ['expenses'],
  bulk_delete_expenses: ['expenses'],
  batch_create_expenses: ['expenses', 'contacts', 'journal_entries'],
  refresh_expenses: ['expenses'],
  create_contact: ['contacts'],
  create_customer: ['contacts'],
  create_supplier: ['contacts'],
  update_contact: ['contacts'],
  delete_contact: ['contacts'],
  merge_contacts: ['contacts'],
  create_sales_invoice: ['invoices', 'contacts', 'journal_entries'],
  create_purchase_invoice: ['invoices', 'contacts', 'journal_entries'],
  create_bill: ['invoices', 'contacts', 'journal_entries'],
  update_invoice: ['invoices', 'journal_entries'],
  delete_invoice: ['invoices', 'journal_entries'],
  void_invoice: ['invoices', 'journal_entries'],
  record_payment: ['invoices', 'payments', 'contacts', 'journal_entries'],
  handle_withdrawal: ['bank_accounts'],
  create_bank_account: ['bank_accounts'],
  update_bank_account: ['bank_accounts'],
  delete_bank_account: ['bank_accounts'],
  record_transfer: ['transfers', 'bank_accounts'],
  update_transfer: ['transfers', 'bank_accounts'],
  delete_transfer: ['transfers', 'bank_accounts'],
  create_chart_account: ['accounts'],
  update_chart_account: ['accounts'],
  delete_chart_account: ['accounts'],
  initialize_chart_of_accounts: ['accounts'],
  create_journal_entry: ['journal_entries', 'accounts'],
  update_journal_entry: ['journal_entries', 'accounts'],
  delete_journal_entry: ['journal_entries', 'accounts'],
  create_quote: ['quotes', 'contacts'],
  convert_quote_to_invoice: ['quotes', 'invoices', 'contacts'],
  adjust_stock: ['inventory'],
  add_inventory: ['inventory'],
  remove_inventory: ['inventory'],
  create_inventory_item: ['inventory'],
  update_inventory_item: ['inventory'],
  delete_inventory_item: ['inventory'],
  list_inventory: ['inventory'],
  search_inventory: ['inventory'],
  filter_inventory: ['inventory'],
  refresh_inventory: ['inventory'],
  create_tax_code: ['tax_periods'],
  create_tax_period: ['tax_periods'],
  update_tax_period: ['tax_periods'],
  delete_tax_period: ['tax_periods'],
  mark_tax_period_filed: ['tax_periods'],
  calculate_vat: ['tax_periods'],
  quick_add_tax_periods: ['tax_periods'],
  refresh_tax_periods: ['tax_periods'],
  reconcile_accounts: ['accounts', 'bank_accounts'],
};

export const getVoiceRefreshResources = (action?: string): VoiceDataResource[] => {
  if (!action) return [];
  return ACTION_RESOURCE_MAP[action] || [];
};

export const shouldDispatchVoiceDataRefresh = (result: VoiceCommandResult): boolean => {
  if (!result.success) return false;
  if (result.needsConfirmation) return false;
  if (
    result.phase === 'gathering_data'
    || result.phase === 'gathering_items'
    || result.phase === 'gathering_quote'
    || result.phase === 'confirmation'
    || result.data?.phase === 'gathering_quote'
  ) {
    return false;
  }
  if (getVoiceRefreshResources(result.action).length > 0) return true;
  return Boolean(
    result.data?.quote_id
    || result.data?.navigateTo === '/quotes'
    || (typeof result.data?.navigateTo === 'string' && result.data.navigateTo.startsWith('/quotes/'))
  );
};

export const dispatchVoiceDataRefresh = (result: VoiceCommandResult): void => {
  let resources = getVoiceRefreshResources(result.action);
  if (resources.length === 0 && (
    result.data?.quote_id
    || result.data?.navigateTo === '/quotes'
    || (typeof result.data?.navigateTo === 'string' && result.data.navigateTo.startsWith('/quotes/'))
  )) {
    resources = ['quotes', 'contacts'];
  }
  if (resources.length === 0) return;

  window.dispatchEvent(new CustomEvent('voiceDataRefresh', {
    detail: {
      action: result.action,
      resources,
      data: result.data,
    },
  }));
};
